import './index.scss'
import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import CONFIG from '../../../../constants/config'

interface Props {
  visible: boolean
  onClose: () => void
  /** 当前可用余额（积分） */
  available: number
  /** 本次查询预计消耗（积分） */
  cost: number
  /** 当前会员等级（CONFIG.VIP_KIND 值 / 0），用于标「当前」 */
  vipKind?: number
  /** 增购积分点击 */
  onBuyPack: () => void
  /** 升级会员点击 */
  onUpgrade: () => void
  /** 弹窗出现回调（visible: false → true 触发一次），调用方用来打曝光埋点 */
  onShow?: () => void
}

/** 会员等级（固定，按设计稿） */
const TIERS = [
  {
    name: '基础会员',
    desc: '团队轻量级查询',
    points: '50万',
    vipKind: CONFIG.VIP_KIND.VIP
  },
  {
    name: '高级会员',
    desc: '适合中小团队',
    points: '600万',
    vipKind: CONFIG.VIP_KIND.VVIP
  },
  {
    name: '超级会员',
    desc: '适合大型团队经常性查询',
    points: '3000万',
    vipKind: CONFIG.VIP_KIND.SVIP
  }
]

/**
 * 积分不足弹窗：余额不够本次查询时拦截发起会话弹出。
 * 展示会员等级对比 + 当前余额 / 本次消耗 + 增购积分 / 升级会员。
 */
const CreditsExhaustedModal: React.FC<Props> = ({
  visible,
  onClose,
  available,
  cost,
  vipKind,
  onBuyPack,
  onUpgrade,
  onShow
}) => {
  // visible: false → true 时触发一次 onShow，调用方据此打曝光埋点；
  // useRef 跟踪上一次 visible 状态，避免每次 visible=true 重渲都报。
  const wasVisibleRef = useRef(false)
  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      wasVisibleRef.current = true
      if (onShow) onShow()
    } else if (!visible) {
      wasVisibleRef.current = false
    }
  }, [visible, onShow])

  if (!visible) return null

  // 超级会员已是最高级：不展示等级对比，只留「增购积分」
  const isSuper = vipKind === CONFIG.VIP_KIND.SVIP

  // Portal 到 body：脱离 mainWrapper 全局 <Spin> 的 children，避免后台 loading 时
  // 被 .ant-spin-blur 半透明化（与 PointsPackModal 同理）
  return ReactDOM.createPortal(
    <div className="credits-exhausted-mask" onClick={onClose}>
      <div
        className="credits-exhausted-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cem-icon">
          <span className="cem-icon-circle" role="img" aria-label="warning">
            ⚠️
          </span>
        </div>
        <div className="cem-title">积分不足,本次填写无法完成</div>
        <div className="cem-subtitle">
          当前积分不足，请升级会员获取更多积分，团队共享
        </div>

        {/* 会员等级对比（超级会员已最高级，不展示） */}
        {!isSuper && (
          <div className="cem-card">
            {TIERS.map((tier, i) => (
              <React.Fragment key={tier.name}>
                {i > 0 && <div className="cem-divider" />}
                <div className="cem-tier">
                  <div className="cem-tier-left">
                    <div className="cem-tier-name-row">
                      <span className="cem-tier-name">{tier.name}</span>
                      {tier.vipKind === vipKind && (
                        <span className="cem-tier-badge">当前</span>
                      )}
                    </div>
                    <div className="cem-tier-desc">{tier.desc}</div>
                  </div>
                  <div className="cem-tier-right">
                    <div className="cem-tier-points">
                      <span className="cem-tier-points-num">{tier.points}</span>
                      <span className="cem-tier-points-unit">积分</span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* 当前余额 + 本次消耗 */}
        <div className="cem-card">
          <div className="cem-bal-row">
            <span className="cem-bal-label">当前余额</span>
            <div className="cem-bal-value">
              <span className="cem-bal-num">
                {available.toLocaleString('en-US')}
              </span>
              <span className="cem-bal-unit">积分</span>
            </div>
          </div>
          <div className="cem-divider" />
          <div className="cem-bal-row">
            <span className="cem-bal-label">本次查询消耗</span>
            <div className="cem-bal-value">
              <span className="cem-cost-num">
                {cost.toLocaleString('en-US')}
              </span>
              <span className="cem-bal-unit">积分</span>
            </div>
          </div>
        </div>

        {/* 按钮：超级会员只「增购积分」；其余「增购积分 + 升级会员」 */}
        <div className="cem-cta-row">
          {isSuper ? (
            <button
              type="button"
              className="cem-btn cem-btn-primary"
              onClick={onBuyPack}
            >
              增购积分
            </button>
          ) : (
            <>
              <button
                type="button"
                className="cem-btn cem-btn-ghost"
                onClick={onBuyPack}
              >
                增购积分
              </button>
              <button
                type="button"
                className="cem-btn cem-btn-primary"
                onClick={onUpgrade}
              >
                升级会员
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CreditsExhaustedModal
